import { ProjectItem } from '../types.ts';

export const getInitialProjectsForCostCenter = (costCenterId?: string, costCenterName?: string): ProjectItem[] => {
  const buName = costCenterName || 'Bajío';
  const ccId = costCenterId || 'CC-MAZDA-BAJIO';

  // Derive concise 3-4 letter prefix for codes (e.g. SLP, BAJ, QRO, GTO, PUE)
  const cleanId = ccId.replace(/^CC-/, '').replace(/[^a-zA-Z0-9]/g, '');
  const prefix = (cleanId.slice(0, 4) || 'PRJ').toUpperCase();

  const isSLP = ccId.toLowerCase().includes('slp') || buName.toLowerCase().includes('luis');
  const isQRO = ccId.toLowerCase().includes('qro') || buName.toLowerCase().includes('queretaro');
  const isPuebla = ccId.toLowerCase().includes('pue') || buName.toLowerCase().includes('puebla');
  const isNorte = ccId.toLowerCase().includes('norte') || buName.toLowerCase().includes('monterrey');

  const mainClient = isSLP 
    ? 'BMW Group / GM San Luis Potosí' 
    : isQRO 
      ? 'Bombardier / Tremec Querétaro'
      : isPuebla
        ? 'Volkswagen / Audi Puebla'
        : isNorte
          ? 'Kia Motors / Metalsa Monterrey'
          : 'Mazda Operaciones Salamanca';

  const secondaryClient = isSLP
    ? 'Planta San Luis Potosí Operaciones'
    : isQRO
      ? 'Planta Querétaro Industrial'
      : 'Nissan Aguascalientes / Bajío';

  return [
    {
      id: `PRJ-${ccId}-001`,
      code: `PRJ-${prefix}-2026-01`,
      name: `Mantenimiento Mayor y Revestimiento de Tina Pirolítica - ${buName}`,
      businessUnit: buName,
      costCenterId: ccId,
      costCenterName: buName,
      clientName: mainClient,
      category: 'MANTENIMIENTO',
      manager: 'Ing. Alejandro Fuentes (Líder Proyecto)',
      sponsor: `Dirección Operativa ${buName}`,
      startDate: '2026-08-01',
      targetEndDate: '2026-09-30',
      status: 'EN_TIEMPO',
      priority: 'ALTA',
      budgetTotal: 485000,
      budgetCurrency: 'MXN',
      description: `Overhaul integral a la tina de decapado y pirolisis térmica en ${buName} con cambio de resistencias cerámicas, sellos de alta temperatura y pruebas de hermeticidad.`,
      scopeObjectives: 'Garantizar disponibilidad operativa del 99.5%, reducir emisiones fugitivas y certificar la cámara según estándares de seguridad industrial.',
      progressPercent: 68,
      createdAt: '2026-08-01T08:00:00.000Z',
      tasks: [
        {
          id: 'tsk-01-1',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '1.1',
          name: 'Levantamiento termográfico y diagnóstico de fracturas',
          phase: 'Fase 1: Diagnóstico e Ingeniería',
          assignee: 'Ing. Carlos Mendoza',
          assigneeRole: 'Especialista Termografía',
          startDate: '2026-08-01',
          endDate: '2026-08-07',
          durationDays: 7,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'ALTA',
          deliverable: 'Reporte NDT y mapa de calor',
          isMilestone: false
        },
        {
          id: 'tsk-01-2',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '1.2',
          name: 'Aprobación de protocolo de bloqueo LOTO y permisos de trabajo en caliente',
          phase: 'Fase 1: Diagnóstico e Ingeniería',
          assignee: 'Lic. Mariana Soto (HSEQ)',
          assigneeRole: 'Coordinador HSEQ',
          startDate: '2026-08-08',
          endDate: '2026-08-11',
          durationDays: 4,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'CRITICA',
          deliverable: 'Permiso STPS firmado',
          isMilestone: true
        },
        {
          id: 'tsk-01-3',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '2.1',
          name: 'Suministro y arribo de refractarios de alta alúmina y sellos grafitados',
          phase: 'Fase 2: Adquisiciones y Preparación',
          assignee: 'Ing. Roberto Silva',
          assigneeRole: 'Compras Técnicas',
          startDate: '2026-08-10',
          endDate: '2026-08-18',
          durationDays: 9,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'MEDIA',
          deliverable: 'Certificados de calidad de material',
          isMilestone: false
        },
        {
          id: 'tsk-01-4',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '3.1',
          name: 'Desmontaje de compuertas y extracción de residuos pirolíticos',
          phase: 'Fase 3: Ejecución Mecánica en Planta',
          assignee: 'Téc. Fernando Ruiz',
          assigneeRole: 'Líder Mecánico',
          startDate: '2026-08-18',
          endDate: '2026-08-25',
          durationDays: 8,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'ALTA',
          deliverable: 'Manifiesto de residuos peligrosos',
          isMilestone: false
        },
        {
          id: 'tsk-01-5',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '3.2',
          name: 'Instalación de nueva coraza refractaria y resistencias de cuarzo',
          phase: 'Fase 3: Ejecución Mecánica en Planta',
          assignee: 'Téc. Fernando Ruiz',
          assigneeRole: 'Líder Mecánico',
          startDate: '2026-08-24',
          endDate: '2026-09-06',
          durationDays: 14,
          progressPercent: 55,
          status: 'EN_PROCESO',
          priority: 'CRITICA',
          deliverable: 'Bitácora de torque y fraguado',
          isMilestone: false
        },
        {
          id: 'tsk-01-6',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '4.1',
          name: 'Calibración de termopares e instrumentación de control PID',
          phase: 'Fase 4: Pruebas y Validación',
          assignee: 'Ing. Karla Domínguez',
          assigneeRole: 'Instrumentista',
          startDate: '2026-09-07',
          endDate: '2026-09-15',
          durationDays: 9,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'MEDIA',
          deliverable: 'Certificado de calibración trazable',
          isMilestone: false
        },
        {
          id: 'tsk-01-7',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '4.2',
          name: 'Prueba de horneado en vacío y rampa de calentamiento a 480°C',
          phase: 'Fase 4: Pruebas y Validación',
          assignee: 'Ing. Alejandro Fuentes',
          assigneeRole: 'Líder Proyecto',
          startDate: '2026-09-16',
          endDate: '2026-09-22',
          durationDays: 7,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'ALTA',
          deliverable: 'Curva térmica validada',
          isMilestone: true
        },
        {
          id: 'tsk-01-8',
          projectId: `PRJ-${ccId}-001`,
          wbsCode: '5.1',
          name: 'Capacitación a supervisores de turno y entrega formal de acta de recepción',
          phase: 'Fase 5: Cierre y Entrega Final',
          assignee: 'Ing. Alejandro Fuentes',
          assigneeRole: 'Líder Proyecto',
          startDate: '2026-09-23',
          endDate: '2026-09-30',
          durationDays: 8,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'ALTA',
          deliverable: 'Acta de entrega recepción firmada',
          isMilestone: true
        }
      ]
    },
    {
      id: `PRJ-${ccId}-002`,
      code: `PRJ-${prefix}-2026-02`,
      name: `Automatización y Monitoreo IoT de Niveles Químicos en Tinajas - ${buName}`,
      businessUnit: buName,
      costCenterId: ccId,
      costCenterName: buName,
      clientName: secondaryClient,
      category: 'AUTOMATIZACION',
      manager: 'Ing. Karla Domínguez (Automatización)',
      sponsor: `Gerencia de Mantenimiento ${buName}`,
      startDate: '2026-07-15',
      targetEndDate: '2026-09-15',
      status: 'EN_RIESGO',
      priority: 'ALTA',
      budgetTotal: 320000,
      budgetCurrency: 'MXN',
      description: `Implementación de red de sensores ultrasónicos, medidores de pH y transmisores LoRaWAN para supervisión en tiempo real de soluciones decapantes y pasivados en ${buName}.`,
      scopeObjectives: 'Eliminar tomas de muestra manuales en zonas calientes, registrar telemetría 24/7 y disparar alertas de dosificación automática.',
      progressPercent: 52,
      createdAt: '2026-07-15T09:00:00.000Z',
      tasks: [
        {
          id: 'tsk-02-1',
          projectId: `PRJ-${ccId}-002`,
          wbsCode: '1.1',
          name: 'Diseño de arquitectura de red y diagramas de conexionado P&ID',
          phase: 'Fase 1: Ingeniería y Selección',
          assignee: 'Ing. Karla Domínguez',
          assigneeRole: 'Automatización',
          startDate: '2026-07-15',
          endDate: '2026-07-28',
          durationDays: 14,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'MEDIA',
          deliverable: 'Plano eléctrico y de red',
          isMilestone: false
        },
        {
          id: 'tsk-02-2',
          projectId: `PRJ-${ccId}-002`,
          wbsCode: '2.1',
          name: 'Adquisición y recepción de sensores de pH resistentes a ácido clorhídrico',
          phase: 'Fase 2: Adquisiciones',
          assignee: 'Lic. Rodrigo Castro',
          assigneeRole: 'Compras',
          startDate: '2026-07-25',
          endDate: '2026-08-15',
          durationDays: 22,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'ALTA',
          deliverable: 'Sensores en almacén técnico',
          isMilestone: false
        },
        {
          id: 'tsk-02-3',
          projectId: `PRJ-${ccId}-002`,
          wbsCode: '3.1',
          name: 'Canalización con tubería conduit cédula 40 y montaje de transmisores',
          phase: 'Fase 3: Instalación de Campo',
          assignee: 'Téc. Gabriel Nava',
          assigneeRole: 'Electricista',
          startDate: '2026-08-12',
          endDate: '2026-08-28',
          durationDays: 17,
          progressPercent: 75,
          status: 'EN_PROCESO',
          priority: 'ALTA',
          deliverable: 'Tendido físico terminado',
          isMilestone: false
        },
        {
          id: 'tsk-02-4',
          projectId: `PRJ-${ccId}-002`,
          wbsCode: '3.2',
          name: 'Integración de gateway LoRaWAN y servidor MQTT local',
          phase: 'Fase 3: Instalación de Campo',
          assignee: 'Ing. Karla Domínguez',
          assigneeRole: 'Automatización',
          startDate: '2026-08-20',
          endDate: '2026-09-02',
          durationDays: 14,
          progressPercent: 30,
          status: 'EN_PROCESO',
          priority: 'CRITICA',
          deliverable: 'Conectividad SCADA',
          isMilestone: false
        },
        {
          id: 'tsk-02-5',
          projectId: `PRJ-${ccId}-002`,
          wbsCode: '4.1',
          name: 'Desarrollo de dashboard SCADA y alarmas a WhatsApp/Email',
          phase: 'Fase 4: Puesta en Marcha',
          assignee: 'Ing. Karla Domínguez',
          assigneeRole: 'Automatización',
          startDate: '2026-09-01',
          endDate: '2026-09-10',
          durationDays: 10,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'MEDIA',
          deliverable: 'Pantalla HMI activa',
          isMilestone: true
        },
        {
          id: 'tsk-02-6',
          projectId: `PRJ-${ccId}-002`,
          wbsCode: '5.1',
          name: 'Pruebas piloto en línea de decapado continuo y entrega final',
          phase: 'Fase 5: Validación y Cierre',
          assignee: 'Ing. Karla Domínguez',
          assigneeRole: 'Automatización',
          startDate: '2026-09-08',
          endDate: '2026-09-15',
          durationDays: 8,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'ALTA',
          deliverable: 'Protocolo de validación SAT',
          isMilestone: true
        }
      ]
    },
    {
      id: `PRJ-${ccId}-003`,
      code: `PRJ-${prefix}-2026-03`,
      name: `Adecuación de Red Contra Incendio y Certificación NOM-002-STPS - ${buName}`,
      businessUnit: buName,
      costCenterId: ccId,
      costCenterName: buName,
      clientName: `Planta ${buName} General`,
      category: 'HSEQ',
      manager: 'Lic. Mariana Soto (Coordinadora HSEQ)',
      sponsor: `Gerencia General ISN ${buName}`,
      startDate: '2026-06-01',
      targetEndDate: '2026-08-30',
      status: 'COMPLETADO',
      priority: 'CRITICA',
      budgetTotal: 590000,
      budgetCurrency: 'MXN',
      description: `Renovación de línea de rociadores automáticos, instalación de gabinetes de manguera de 30m, motobomba diésel de respaldo y certificación en ${buName}.`,
      scopeObjectives: 'Cumplimiento normativo total con la NOM-002-STPS-2010 y protección del 100% de la nave de almacenamiento de racks.',
      progressPercent: 100,
      createdAt: '2026-06-01T08:00:00.000Z',
      tasks: [
        {
          id: 'tsk-03-1',
          projectId: `PRJ-${ccId}-003`,
          wbsCode: '1.1',
          name: 'Memoria de cálculo hidráulico bajo estándar NFPA 13 y 20',
          phase: 'Fase 1: Ingeniería',
          assignee: 'Ing. Perito Certificado',
          assigneeRole: 'Perito Protección Civil',
          startDate: '2026-06-01',
          endDate: '2026-06-15',
          durationDays: 15,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'CRITICA',
          deliverable: 'Memoria de cálculo avalada',
          isMilestone: false
        },
        {
          id: 'tsk-03-2',
          projectId: `PRJ-${ccId}-003`,
          wbsCode: '2.1',
          name: 'Tendido e interconexión de tubería ranurada cédula 40 de 4 pulgadas',
          phase: 'Fase 2: Instalación de Tuberías',
          assignee: 'Téc. Pailero Especialista',
          assigneeRole: 'Pailero Soldador',
          startDate: '2026-06-16',
          endDate: '2026-07-20',
          durationDays: 35,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'ALTA',
          deliverable: 'Línea aérea presurizada',
          isMilestone: false
        },
        {
          id: 'tsk-03-3',
          projectId: `PRJ-${ccId}-003`,
          wbsCode: '3.1',
          name: 'Prueba de presión hidrostática a 200 PSI durante 4 horas continuas',
          phase: 'Fase 3: Pruebas y Certificación',
          assignee: 'Lic. Mariana Soto',
          assigneeRole: 'Coordinador HSEQ',
          startDate: '2026-07-22',
          endDate: '2026-07-25',
          durationDays: 4,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'CRITICA',
          deliverable: 'Certificado de estanqueidad',
          isMilestone: true
        },
        {
          id: 'tsk-03-4',
          projectId: `PRJ-${ccId}-003`,
          wbsCode: '4.1',
          name: 'Auditoría final y emisión de Dictamen Favorable NOM-002',
          phase: 'Fase 4: Cierre Normativo',
          assignee: 'Lic. Mariana Soto',
          assigneeRole: 'Coordinador HSEQ',
          startDate: '2026-08-01',
          endDate: '2026-08-15',
          durationDays: 15,
          progressPercent: 100,
          status: 'COMPLETADA',
          priority: 'CRITICA',
          deliverable: 'Dictamen oficial STPS',
          isMilestone: true
        }
      ]
    },
    {
      id: `PRJ-${ccId}-004`,
      code: `PRJ-${prefix}-2026-04`,
      name: `Línea de Recuperación y Pintura Electrostática E-Coat para Racks - ${buName}`,
      businessUnit: buName,
      costCenterId: ccId,
      costCenterName: buName,
      clientName: isSLP ? 'BMW Group SLP / Tier-1' : isQRO ? 'Safran / Tier-1 QRO' : 'Toyota Guanajuato / Tier-1',
      category: 'SERVICIOS_ESPECIALIZADOS',
      manager: 'Ing. Samuel Godínez (Operaciones Especiales)',
      sponsor: `Dirección Comercial ISN ${buName}`,
      startDate: '2026-08-20',
      targetEndDate: '2026-11-15',
      status: 'POR_INICIAR',
      priority: 'MEDIA',
      budgetTotal: 720000,
      budgetCurrency: 'MXN',
      description: `Habilitación de celda semicontinua para aplicación de recubrimiento anticorrosivo epóxico electroforético en sede ${buName}.`,
      scopeObjectives: 'Aumentar la vida útil de los racks a 5 años sin corrosión y cumplir pruebas de 500 horas en cámara de niebla salina ASTM B117.',
      progressPercent: 10,
      createdAt: '2026-08-20T10:00:00.000Z',
      tasks: [
        {
          id: 'tsk-04-1',
          projectId: `PRJ-${ccId}-004`,
          wbsCode: '1.1',
          name: 'Diseño de ganchos de colgado y balanceo de línea de transporte aéreo',
          phase: 'Fase 1: Ingeniería y Lay-out',
          assignee: 'Ing. Samuel Godínez',
          assigneeRole: 'Operaciones Especiales',
          startDate: '2026-08-20',
          endDate: '2026-09-05',
          durationDays: 17,
          progressPercent: 40,
          status: 'EN_PROCESO',
          priority: 'MEDIA',
          deliverable: 'Plano de colgado de racks',
          isMilestone: false
        },
        {
          id: 'tsk-04-2',
          projectId: `PRJ-${ccId}-004`,
          wbsCode: '2.1',
          name: 'Instalación de rectificador de corriente DC de 400V y electrodos de acero 316',
          phase: 'Fase 2: Equipamiento Eléctrico',
          assignee: 'Téc. Gabriel Nava',
          assigneeRole: 'Electricista',
          startDate: '2026-09-06',
          endDate: '2026-09-25',
          durationDays: 20,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'ALTA',
          deliverable: 'Banco de rectificación probado',
          isMilestone: false
        },
        {
          id: 'tsk-04-3',
          projectId: `PRJ-${ccId}-004`,
          wbsCode: '3.1',
          name: 'Carga química inicial de resina epóxica catódica y agua desmineralizada',
          phase: 'Fase 3: Preparación de Baño',
          assignee: 'Quím. Brenda Alatorre',
          assigneeRole: 'Química de Procesos',
          startDate: '2026-09-26',
          endDate: '2026-10-10',
          durationDays: 15,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'ALTA',
          deliverable: 'Hoja de parámetros químicos estables',
          isMilestone: true
        },
        {
          id: 'tsk-04-4',
          projectId: `PRJ-${ccId}-004`,
          wbsCode: '4.1',
          name: 'Prueba de curado en horno túnel y espesor de película seca (0.8 - 1.2 mils)',
          phase: 'Fase 4: Pruebas de Calidad',
          assignee: 'Ing. Samuel Godínez',
          assigneeRole: 'Operaciones Especiales',
          startDate: '2026-10-11',
          endDate: '2026-10-25',
          durationDays: 15,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'MEDIA',
          deliverable: 'Reporte de laboratorio de adherencia cruzada',
          isMilestone: false
        },
        {
          id: 'tsk-04-5',
          projectId: `PRJ-${ccId}-004`,
          wbsCode: '5.1',
          name: 'Inicio de producción masiva de racks automotrices y validación PPAP',
          phase: 'Fase 5: Liberación de Producción',
          assignee: 'Ing. Samuel Godínez',
          assigneeRole: 'Operaciones Especiales',
          startDate: '2026-10-26',
          endDate: '2026-11-15',
          durationDays: 21,
          progressPercent: 0,
          status: 'POR_INICIAR',
          priority: 'CRITICA',
          deliverable: 'Aprobación PPAP Nivel 3',
          isMilestone: true
        }
      ]
    }
  ];
};
